<script lang="ts" setup>
import { breakpointsConfig } from '~/utils/css/breakpoints'

defineProps<{
  quickFiltersKey: string
  hideQuickFilters?: boolean
}>()

const facetStore = useFacetStore()
const headerStore = useHeaderStore()
const { filterBarDisabled } = storeToRefs(facetStore)
const { headerHidden } = storeToRefs(headerStore)
const isTablet = useMediaQuerySSR(`(min-width: ${breakpointsConfig.tablet}px)`)

/* FilterControls - Sorting */
const { activeFiltersCount } = storeToRefs(useFilterStore())
const { showFilterMenu } = storeToRefs(useModalStore())
const { sortOptionsForDropdown, selectedSortOptionForDropdown, selectSortOption } = useProductSortingOptions()
function toggleFilterMenu() {
  showFilterMenu.value = !showFilterMenu.value
}

/* FilterControls - Grid */
const { gridOptions, selectedGridOption, selectGridOption } = useProductGridOptions()

/* Keep track of controls becoming sticky */
const controlsSticky = ref<boolean>(false)
const controlsEl = ref<HTMLElement>()
function checkIfControlsAreSticky() {
  const scrolledPastControls = window.scrollY > (controlsEl.value?.offsetTop || 0)
  controlsSticky.value = scrolledPastControls
}
useEventListener('scroll', checkIfControlsAreSticky)
onMounted(() => checkIfControlsAreSticky())

/* Hide header on scroll, but only when controls are sticky */
onBeforeUnmount(() => (headerStore.hideHeaderOnScroll = false))
watch(controlsSticky, () => {
  headerStore.hideHeaderOnScroll = !isTablet.value && controlsSticky.value
}, { immediate: true })
</script>

<template>
  <div
    class="filter-bar"
    :class="[{ 'filter-bar--disabled': filterBarDisabled }]"
    data-test="filters-bar"
  >
    <div class="filter-bar__quick-filters">
      <FilterQuickFilters v-if="!hideQuickFilters" :quick-filters-key="quickFiltersKey"/>
    </div>

    <div ref="controlsEl" class="filter-bar__controls">
      <FilterControls
        :sort-options="sortOptionsForDropdown"
        :grid-options="gridOptions"
        :active-sort-option="selectedSortOptionForDropdown"
        :active-grid-option="selectedGridOption"
        :active-filters-count="activeFiltersCount"
        @toggle-filter-menu="toggleFilterMenu"
        @sort-option-selected="o => selectSortOption(o)"
        @grid-option-selected="o => selectGridOption(o)"
      />
    </div>
  </div>

  <div
    class="sticky-controls" :class="{
      'sticky': controlsSticky,
      'header-hidden': headerHidden,
    }"
  >
    <FilterControls
      :sort-options="sortOptionsForDropdown"
      :grid-options="gridOptions"
      :active-sort-option="selectedSortOptionForDropdown"
      :active-grid-option="selectedGridOption"
      :active-filters-count="activeFiltersCount"
      @toggle-filter-menu="toggleFilterMenu"
      @sort-option-selected="o => selectSortOption(o)"
      @grid-option-selected="o => selectGridOption(o)"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '~/assets/scss/rules/breakpoints';

.filter-bar {
  display: flex;
  flex-direction: column;
  margin-top: 0.4rem;

  @media (min-width: $tablet) {
    flex-direction: row-reverse;
    justify-content: space-between;
    gap: 150px;
    margin-top: 0.6rem;
    margin-left: 0.8rem;
  }

  // Disabled state
  & > * {
    opacity: 1;
    will-change: opacity;
    transition: opacity 0.3s ease;
  }
  &--disabled > * {
    pointer-events: none;
    opacity: 0.3;
  }

  // General styling
  &__quick-filters,
  &__controls {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 5rem;
  }

  &__controls {
    margin-left: 0.4rem;
    flex-shrink: 0;
  }

  // Fix overflow
  &__quick-filters {
    display: grid;
    grid-template-columns: 1fr;
  }
}

.sticky-controls {
  --translateX: -150%;
  --translateY: var(--header-main-height);

  position: fixed;
  top: 0;
  left: 1.5rem;
  z-index: 2;
  background-color: var(--gray);
  border-radius: 2px;
  transform: translate(var(--translateX), var(--translateY));
  transition: transform 0.3s, opacity 0.3s;
  pointer-events: none;

  @media (min-width: $tablet) {
    left: 2rem;
  }

  &.header-hidden {
    --translateY: 1.5rem;
  }
  &.sticky {
    --translateX: 0;
    pointer-events: auto;
  }
}
</style>
